import {
    ActionMenu,
    Avatar,
    ButtonSecondary,
    ButtonPrimary,
    Card,
    CircularProgress,
    Colors,
    formatDate,
    getDayOfMonth,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    Typography,
    subtleHover,
    type OptionProps,
    type IconName,
} from '@amelio/ui-library';

import { AvatarStack } from '../AvatarStack/AvatarStack';
import { InfoBadge } from '../InfoBadge/InfoBadge';
import { ReviewersStack } from '../ReviewersStack/ReviewersStack';
import { UserCard } from '../UserCard/UserCard';

import type { User } from '@/types/directory/User';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import i18n from '@/plugins/i18n';
import { getInitials, getProfilePictureUrl } from '@/utils/user';

export interface EvaluationInfoBadge {
    /** Unique identifier for the badge */
    id: string;
    /** Icon name from the UI library */
    icon?: IconName;
    /** Primary label text (e.g., "Analyst", "360 Evaluation") */
    primaryLabel: string;
    /** Optional secondary label text (e.g., "Profile", "Template") */
    secondaryLabel?: string;
    /** Optional callback when badge is clicked */
    onClick?: () => void;
}

export interface EvaluationPrimaryAction {
    /** Button label text */
    label: string;
    /** Click handler */
    onClick: () => void;
    /** Button variant */
    variant?: 'filled' | 'outlined';
}

export interface EvaluationCardProps {
    /** Variant type: 'Ongoing360' shows progress, 'ToBeLaunched360' shows primary action */
    variant?: 'Ongoing360' | 'ToBeLaunched360';
    /** Single user or array of users for the main section */
    user?: User;
    /** Array of users (for multiple reviewees in ToBeLaunched360) */
    users?: User[];
    /** Array of reviewers/collaborators */
    reviewers?: User[];
    /** Label for reviewers count (e.g., "8 reviewers") */
    reviewersLabel?: string;
    /** Reviewers of an anonymous feedback: only counted, never shown. Takes over `reviewers`. */
    anonymousReviewerCount?: number;
    /** Array of info badges for metadata */
    infoBadges?: EvaluationInfoBadge[];
    /** Progress percentage (0-100) - only displayed in Ongoing360 variant */
    progress?: number;
    /** Primary action button - only displayed in ToBeLaunched360 variant */
    primaryAction?: EvaluationPrimaryAction;
    /** Action menu items - only displayed in Ongoing360 variant */
    actionMenuItems: OptionProps[];
    /** Date information */
    date?: string;
    /** Background color class */
    backgroundColor?: string;
    /** Additional CSS classes */
    className?: string;
    /** Data-testid attribute for testing */
    dataTestId?: string;
    /** Optional callback when entire card is clicked (e.g., navigate to results) */
    onCardClick?: () => void;
}

/**
 * Determines if the user section should display AvatarStack or UserCard.
 *
 * @param users - Array of users
 * @returns True if should use AvatarStack (multiple users)
 */
const shouldUseAvatarStack = (users: User[] | undefined): boolean => {
    return Boolean(users && users.length > 1);
};

/**
 * Determines if reviewers section should be displayed.
 *
 * @param reviewers - Array of reviewer users
 * @returns True if reviewers exist and array is not empty
 */
const shouldDisplayReviewers = (reviewers: User[] | undefined): boolean => {
    return Boolean(reviewers && reviewers.length > 0);
};

/**
 * EvaluationCard component displays comprehensive evaluation information in a horizontal card format.
 *
 * Features:
 * - Two variants: Ongoing360 (with progress) and ToBeLaunched360 (with primary action)
 * - User identity (single via UserCard or multiple via AvatarStack)
 * - Reviewers display via AvatarStack
 * - Multiple info badges for metadata
 * - Conditional progress indicator (Ongoing360)
 * - Conditional primary action button (ToBeLaunched360)
 * - Action menu (kebab) for additional options
 * - Date display with variant-specific colors
 */
const VARIANT_ONGOING = 'Ongoing360' as const;
const VARIANT_TO_BE_LAUNCHED = 'ToBeLaunched360' as const;

const AVATAR_STACK_MAX_VISIBLE = 4;
const REVIEWEE_AVATAR_STACK_MAX_VISIBLE = 2;
const PROGRESS_SIZE = 70;
const PROGRESS_THICKNESS = 4;

export const EvaluationCard = ({
    variant = VARIANT_ONGOING,
    user,
    users,
    reviewers,
    reviewersLabel,
    anonymousReviewerCount,
    infoBadges,
    progress,
    primaryAction,
    actionMenuItems,
    date,
    backgroundColor = 'bg-white/70',
    className = '',
    dataTestId = 'evaluation-card',
    onCardClick,
}: EvaluationCardProps) => {
    const { getJobTitle } = useCompanyInformation();
    const { t } = useAmelioTranslations();

    const isOngoing360 = variant === VARIANT_ONGOING;
    const isToBeLaunched360 = variant === VARIANT_TO_BE_LAUNCHED;
    const useAvatarStack = shouldUseAvatarStack(users);
    const isAnonymous = anonymousReviewerCount !== undefined;
    const hasReviewers = isAnonymous || shouldDisplayReviewers(reviewers);
    const hasUserData = Boolean(user || (users && users.length > 0));
    const reviewersCount = anonymousReviewerCount ?? reviewers?.length ?? 0;

    const fullName = user ? `${user.firstName} ${user.lastName}` : '';
    const userTitle = user ? getJobTitle(user.jobTitleId || '') : '';
    const avatarSize = 'xs';
    const avatarBorderColor = isToBeLaunched360 ? Colors.SECONDARY_600 : Colors.PRIMARY;
    const avatarBorderSize = isToBeLaunched360 ? 'ring-2' : 'ring-1';

    const wrapperClassName = `relative flex flex-col sm:flex-row w-full items-center lg:items-center my-5 lg:${className}${onCardClick ? ` cursor-pointer ${subtleHover}` : ''}`;

    const handleKeyDown = onCardClick
        ? (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCardClick();
              }
          }
        : undefined;

    const stopPropagation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            className={wrapperClassName}
            data-testid={dataTestId}
            role={onCardClick ? 'button' : 'article'}
            tabIndex={onCardClick ? 0 : undefined}
            onClick={onCardClick}
            onKeyDown={handleKeyDown}
        >
            {/* Main Container - White background with all content except date */}
            <Card
                variant={'flat'}
                padding={'lg'}
                orientation={'horizontal'}
                borderRadius={'2xl'}
                bgColor={backgroundColor}
                className={`order-3 flex w-full min-w-0 flex-1 items-center justify-between border border-white sm:order-1 lg:h-25 lg:max-xl:p-4`}
            >
                <div
                    className={
                        'flex w-full flex-col items-center justify-between gap-4 sm:flex-nowrap sm:gap-6 lg:flex-row lg:gap-4'
                    }
                >
                    <div
                        className={
                            'order-2 mb-4 flex w-full flex-wrap items-center justify-between gap-2 sm:mb-0 sm:flex-nowrap sm:gap-0 md:order-1 lg:w-auto'
                        }
                    >
                        {/* User Section: UserCard or AvatarStack */}

                        <div
                            data-testid={`${dataTestId}-user-section`}
                            className={'shrink-0 pl-0 sm:w-48 lg:w-36'}
                        >
                            {hasUserData ? (
                                <div>
                                    {/* Multiple users: display AvatarStack */}
                                    {useAvatarStack && users && (
                                        <AvatarStack
                                            users={users}
                                            maxVisible={REVIEWEE_AVATAR_STACK_MAX_VISIBLE}
                                            size={'2xs'}
                                            spacing={'tight'}
                                            showBorder
                                            borderColor={Colors.SECONDARY_600}
                                            borderSize={'ring-2'}
                                            showTooltip
                                            dataTestId={`${dataTestId}-user-stack`}
                                        />
                                    )}

                                    {/* Single user: display UserCard. `min-w-0` lets the
                                        column shrink inside the card so the name/title can
                                        clip instead of overflowing; the tooltip reveals the
                                        full text on hover. */}
                                    {!useAvatarStack && user && (
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div className={'min-w-0'}>
                                                        <UserCard
                                                            avatar={
                                                                <Avatar
                                                                    src={getProfilePictureUrl(
                                                                        user?.profilePictureUrl ||
                                                                            '',
                                                                    )}
                                                                    initials={getInitials(user)}
                                                                    alt={fullName}
                                                                    size={avatarSize}
                                                                    className={
                                                                        'lg:max-xl:h-10 lg:max-xl:w-10'
                                                                    }
                                                                    variant={'circular'}
                                                                    borderColor={avatarBorderColor}
                                                                    borderSize={avatarBorderSize}
                                                                />
                                                            }
                                                            name={fullName}
                                                            description={userTitle}
                                                            size={'small'}
                                                            nameWeight={'medium'}
                                                            nameClassName={
                                                                'text-body-sm truncate overflow-hidden whitespace-nowrap'
                                                            }
                                                            descriptionClassName={
                                                                'text-caption truncate overflow-hidden whitespace-nowrap block'
                                                            }
                                                            compact
                                                            alt={`${fullName} profile`}
                                                            textColor={Colors.BASE}
                                                            descriptionTextColor={Colors.BASE_400}
                                                        />
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    side={'top'}
                                                    className={'pointer-events-auto max-w-md'}
                                                >
                                                    <p>{fullName}</p>
                                                    {userTitle && <p>{userTitle}</p>}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    )}
                                </div>
                            ) : (
                                <UserCard
                                    avatar={
                                        <Avatar
                                            src={undefined}
                                            alt={t('common.unassigned')}
                                            size={avatarSize}
                                            className={'lg:max-xl:h-10 lg:max-xl:w-10'}
                                            variant={'circular'}
                                            borderColor={avatarBorderColor}
                                            borderSize={avatarBorderSize}
                                        />
                                    }
                                    name={t('common.unassigned')}
                                    size={'small'}
                                    nameWeight={'medium'}
                                    nameClassName={'text-body-sm'}
                                    compact
                                    alt={`${t('common.unassigned')} profile`}
                                    textColor={Colors.BASE}
                                    descriptionTextColor={Colors.BASE_400}
                                />
                            )}
                        </div>

                        {/* Reviewers Section: AvatarStack + Count Label */}

                        <div
                            className={'flex w-24 shrink-0 flex-col items-center gap-1 pt-1'}
                            data-testid={`${dataTestId}-reviewers`}
                            role={'region'}
                            aria-label={t('common.reviewersSectionAriaLabel')}
                        >
                            {hasReviewers && (
                                <div
                                    className={`flex w-full items-center justify-center ${isAnonymous ? 'gap-1.5' : 'flex-col text-center'}`}
                                >
                                    <ReviewersStack
                                        isAnonymous={isAnonymous}
                                        users={reviewers as User[]}
                                        maxVisible={AVATAR_STACK_MAX_VISIBLE}
                                        size={'4xs'}
                                        spacing={'tight'}
                                        showBorder
                                        borderColor={Colors.BASE_50}
                                        borderSize={'ring-1'}
                                        showTooltip
                                        dataTestId={`${dataTestId}-reviewers-stack`}
                                    />

                                    <Text
                                        size={'small'}
                                        weight={'regular'}
                                        color={Colors.BASE_400}
                                        className={`text-caption block leading-tight whitespace-normal ${isAnonymous ? '' : 'w-full text-center'}`}
                                    >
                                        {reviewersLabel ||
                                            t(
                                                reviewersCount === 1
                                                    ? 'common.reviewerWithCount'
                                                    : 'common.reviewersWithCount',
                                                { count: reviewersCount },
                                            )}
                                    </Text>
                                </div>
                            )}

                            {!hasReviewers && reviewers !== undefined && (
                                <Text
                                    size={'small'}
                                    weight={'regular'}
                                    color={Colors.BASE_400}
                                    className={
                                        'text-caption block w-full text-center leading-tight whitespace-normal'
                                    }
                                >
                                    {t('common.unassigned')}
                                </Text>
                            )}
                        </div>
                    </div>

                    <div
                        className={`order-3 flex w-full min-w-0 flex-1 flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-2 md:order-2 lg:w-auto`}
                    >
                        {/* Info Badges Section */}
                        {infoBadges && infoBadges.length > 0 && (
                            <>
                                {infoBadges.map((badge, index) => (
                                    <InfoBadge
                                        key={badge.id}
                                        icon={badge.icon}
                                        primaryLabel={badge.primaryLabel}
                                        secondaryLabel={badge.secondaryLabel}
                                        size={'sm'}
                                        ariaLabel={`${badge.primaryLabel}${badge.secondaryLabel ? `: ${badge.secondaryLabel}` : ''}`}
                                        onClick={badge.onClick}
                                        className={`w-full flex-1 lg:w-auto ${index === 1 ? 'justify-end lg:justify-start' : 'justify-start'}`}
                                    />
                                ))}
                            </>
                        )}

                        {/* Progress Indicator - Only in Ongoing360 variant */}
                        {isOngoing360 && progress !== undefined && (
                            <div
                                className={
                                    'flex flex-1 justify-end sm:justify-end lg:justify-start'
                                }
                                data-testid={`${dataTestId}-progress`}
                            >
                                <CircularProgress
                                    value={progress}
                                    size={PROGRESS_SIZE}
                                    thickness={PROGRESS_THICKNESS}
                                    completedColor={Colors.SECONDARY}
                                    remainingColor={Colors.PRIMARY}
                                    direction={'counter-clockwise'}
                                    showPercentage
                                    dataTestId={`${dataTestId}-progress-indicator`}
                                />
                            </div>
                        )}
                    </div>

                    {/* Primary Action Button - Only in ToBeLaunched360 variant */}
                    <div
                        className={`${primaryAction ? 'justify-between' : 'justify-end'} order-1 flex w-full items-center gap-2 md:order-3 lg:w-auto`}
                        onClick={stopPropagation}
                    >
                        {primaryAction && (
                            <div
                                className={'flex-1'}
                                data-testid={`${dataTestId}-primary-action`}
                            >
                                {primaryAction.variant === 'outlined' ? (
                                    <ButtonSecondary
                                        size={'sm'}
                                        className={
                                            'lg:max-xl:w-[100px] lg:max-xl:text-[12px] lg:max-xl:leading-tight lg:max-xl:whitespace-normal'
                                        }
                                        onClick={primaryAction.onClick}
                                        dataTestId={`${dataTestId}-primary-button`}
                                    >
                                        {primaryAction.label}
                                    </ButtonSecondary>
                                ) : (
                                    <ButtonPrimary
                                        size={'md'}
                                        className={
                                            'lg:max-xl:w-[100px] lg:max-xl:text-[12px] lg:max-xl:leading-tight lg:max-xl:whitespace-normal'
                                        }
                                        onClick={primaryAction.onClick}
                                        dataTestId={`${dataTestId}-primary-button`}
                                    >
                                        {primaryAction.label}
                                    </ButtonPrimary>
                                )}
                            </div>
                        )}

                        {/* Action Menu (Kebab) */}
                        <div
                            className={'shrink-0'}
                            data-testid={`${dataTestId}-action-menu`}
                        >
                            <ActionMenu
                                items={actionMenuItems}
                                placement={'top-right'}
                                size={'sm'}
                                variant={'default'}
                                ariaLabel={'Evaluation actions menu'}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Connector between Card and Date */}
            {date && (
                <div
                    className={`${backgroundColor} order-2 h-3 w-3 border-y border-white sm:order-2`}
                />
            )}

            {/* Date Container - Separated white container */}
            {date && (
                <div
                    className={`${backgroundColor} order-1 flex h-25 w-24 flex-col items-center justify-center rounded-2xl border border-white p-3 backdrop-blur sm:order-3`}
                    data-testid={`${dataTestId}-date`}
                    role={'region'}
                    aria-label={t('common.evaluationDateAriaLabel')}
                >
                    <Typography
                        variant={'h1'}
                        weight={'bold'}
                        color={isToBeLaunched360 ? Colors.SECONDARY : Colors.PRIMARY}
                        className={'leading-none'}
                    >
                        {getDayOfMonth({ date, language: i18n.language })}
                    </Typography>

                    <Typography
                        variant={'body-small'}
                        weight={'regular'}
                        color={Colors.BASE}
                        className={'text-caption mt-1'}
                    >
                        {formatDate({
                            date,
                            format: 'MMMM',
                            language: i18n.language,
                        })}
                    </Typography>
                </div>
            )}
        </div>
    );
};
